// ═════════════════════════════════════════════════════════════════════════════
// NHCX Payment Notice API client
//
// Contract: PAYMENT_NOTICE_UI_INTEGRATION.md (repo root).
//
//   GET  /app/api/insurance/responses/                      → inbox
//   GET  /app/api/insurance/response/{id}/                  → one cached notice
//   POST /app/api/insurance/payment-notice/on_request/      → acknowledge
//   POST /app/api/insurance/dummy-payer/payment-notice-init/  → sandbox only
//
// The inbound POST /paymentnotice/request is payer-initiated — the UI never
// calls it. Ack takes no Org-Code / App-Mode. Nothing arrives after a successful
// ack; the exchange is closed.
// ═════════════════════════════════════════════════════════════════════════════

import axios from "axios";
import {
  nhcxUrl,
  nhcxHeaders,
  nhcxAckHeaders,
  listNhcxResponses,
  pollNhcxResponse,
  normalizeNhcxError,
} from "./nhcx-client";

export { listNhcxResponses, pollNhcxResponse };

export function isPaymentNotice(item) {
  return String(item?.use_case || "") === "payment_notice";
}

export async function listPaymentNotices() {
  const all = await listNhcxResponses();
  return all.filter(isPaymentNotice);
}

export async function acknowledgePaymentNotice({ correlationId, description, overridePatientAbhaId }) {
  const body = { correlation_id: correlationId };
  if (String(description || "").trim()) body.description = String(description).trim();
  if (String(overridePatientAbhaId || "").trim()) {
    body.override_patient_abha_id = String(overridePatientAbhaId).trim();
  }
  const res = await axios.post(nhcxUrl("/payment-notice/on_request/"), body, {
    headers: nhcxAckHeaders(),
  });
  return res.data;
}

/**
 * Sandbox: the dummy payer posts an inbound notice under a new correlation id.
 * Watch GET /responses for use_case=payment_notice rather than polling submit.
 */
export async function initDummyPaymentNotice({ providerId, claimNumber }) {
  const res = await axios.post(
    nhcxUrl("/dummy-payer/payment-notice-init/"),
    { provider_id: providerId, claim_number: claimNumber || "" },
    { headers: nhcxHeaders() }
  );
  return res.data;
}

export function normalizeNoticeError(err, kind = "ack") {
  if (kind === "list") {
    return normalizeNhcxError(err, {
      404: "No cached payment notices on the insurance service yet.",
      502: "The NHCX gateway is unreachable. Try again in a moment.",
    });
  }
  return normalizeNhcxError(err, {
    404:
      "This notice is gone from the service's memory (a restart clears it). The payment itself is unaffected — only the acknowledgement is lost. The payer will need to re-send; retrying will not reconstruct it.",
    409: "This notice is not in a state that can be acknowledged. Refresh the inbox.",
    500: "Organisation details could not be recovered from the cached notice. Quote the correlation id to support.",
    502: "The NHCX gateway is unreachable. Try again in a moment.",
  });
}
